import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [lang, setLangState] = useState(() => localStorage.getItem("lumen_lang") || "en");
  const [version, setVersionState] = useState(() => {
    const v = localStorage.getItem("lumen_version");
    return v === "NVI" ? "RV1960" : v || "KJV";
  });
  const [fontSize, setFontSizeState] = useState(() => {
    const v = parseInt(localStorage.getItem("lumen_font_size") || "14", 10);
    return isNaN(v) ? 14 : Math.max(10, Math.min(18, v));
  });

  // Apply global font size — scales the whole app by changing html font-size.
  // Tailwind uses rem units, so bumping html font-size scales everything proportionally
  // (Bible verses, plan cards, community posts, prayers, groups, bookmarks, buttons, etc.)
  useEffect(() => {
    // Baseline: fontSize=14 → html font-size stays at 16px (browser default = 1rem)
    // Range 10..18pt maps to ~11.4..20.6 px html font-size (proportional scale)
    const scale = fontSize / 14;
    document.documentElement.style.fontSize = `${16 * scale}px`;
    document.documentElement.style.setProperty("--app-font-size", `${fontSize}px`);
    document.documentElement.style.setProperty("--app-font-scale", String(scale));
    document.documentElement.setAttribute("data-font-size", String(fontSize));
  }, [fontSize]);

  // Keep the user's timezone + language on the server for the 3am devotional reminder.
  useEffect(() => {
    if (!user) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz) api.post("/me/locale", { timezone: tz, lang }).catch(() => {});
  }, [user?.user_id, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFontSize = (n) => {
    const clamped = Math.max(10, Math.min(18, parseInt(n, 10) || 14));
    setFontSizeState(clamped);
    localStorage.setItem("lumen_font_size", String(clamped));
  };

  const setLang = (l) => {
    setLangState(l);
    localStorage.setItem("lumen_lang", l);
    // Auto-switch default version if the current one doesn't match language
    if (l === "es" && (version === "KJV" || version === "WEB")) {
      setVersion("RV1960");
    }
    if (l === "en" && (version === "RV1960" || version === "RV2004")) {
      setVersion("KJV");
    }
  };

  const setVersion = (v) => {
    setVersionState(v);
    localStorage.setItem("lumen_version", v);
  };

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setAuthLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    window.location.href = "/";
  };

  return (
    <AppCtx.Provider
      value={{ user, setUser, authLoading, lang, setLang, version, setVersion, fontSize, setFontSize, checkAuth, logout }}
    >
      {children}
    </AppCtx.Provider>
  );
}

export const useApp = () => useContext(AppCtx);
